/**
 * SUPERVISOR.ACCOUNTS
 * List of IDTree account
 */

import { useEffect, useState } from "react"
import { connect } from "react-redux"
import Button from "@/components/button"
import { WithTranslation, withTranslation } from "react-i18next"
import PageHeader from "@/components/page-header"
import Card from "@/components/card"
import ListContainer from "@/components/list-container"
import { ACCOUNT_DEMO, ACCOUNT_DEMO_COLOR, ACCOUNT_DEMO_GROUP, Account, AccountState } from "@/redux/account.types"
import {
  accountStatus,
  accountFetch_AsSupervisor,
  accountFetchCount_AsSupervisor,
  accountGetCount,
  accountGetCountSearch,
  accountAdd,
  accountActivate,
  accountGet
} from "@/redux/account.actions"
import { store } from "@/index"
import { STATUS_LOADED, STATUS_LOADING, STATUS_LOAD_ERROR, STATUS_SEARCHING } from "@/redux/_status.types"
import ListItem from "@/components/list-item"
import ListFields from "@/components/list-fields"
import Chip from "@/components/chip"
import ListCell from "@/components/list-cell"
import Space from "@/components/space"
import AddAccountModal from "@/modals/add-account.modal"
import { faPencilAlt, faPlayCircle, faShieldAlt } from "@fortawesome/free-solid-svg-icons"
import ListButton from "@/components/list-button"
import Tabs from "@/components/tabs"
import ListIcon from "@/components/list-icon"
import { subscriptionGet } from "@/redux/subscription.actions"
import PageLoader from "@/components/page-loader"
import { Session } from "@/redux/_session.types"
import SupervisorAccountConnectModal from "@/modals/supervisor-account-connect.modal"
import { userActivate } from "@/redux/user.actions"
import DeleteUserSuperModal from "@/modals/delete-user-as-supervisor.modal"
import SupervisorsListWidget from "@/widgets/supervisors-list.widget"
import { User } from "@/redux/user.types"
import NavSupervisor from "@/nav/nav.supervisor"
import ModulesWidget from "@/widgets/modules.widget"
import SendingsAllWidget from "@/widgets/sendings-all.widget"
import { NavigateFunction, useNavigate } from "react-router-dom"
import Checkbox from "@/components/checkbox"
import { formatDate } from "@/utils/format-date.utils"
import { getImageUrl } from "@/utils/get-image-url"

interface Props extends WithTranslation {
  _session: Session
  account: AccountState
}

//Modals
const MODAL_ADD = "MODAL_ADD" //Add new account
const MODAL_AUTH_CONFIRM = "MODAL_AUTH_CONFIRM" //Confirm connexion
const MODAL_DELETE_USER = "MODAL_DELETE_USER" //Display user to delete

//Tabs
//const TAB_LOW = "TAB_LOW" //See all low touch client (from self-service)
const TAB_MED = "TAB_MED" //See all med touch clients (create by supervisor)
const TAB_SELF = "TAB_SELF" //See my clients
const TAB_SEND = "TAB_SEND" //Show upcoming sendings
const TAB_USERS = "TAB_USERS" //Show admins

//Limit of account displayed by page
const LIMIT: number = 5

function SupervisorAccounts(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()

  const queryParams: URLSearchParams = new URLSearchParams(window.location.search)
  const tab: string | null = queryParams.get("tab")

  const [currentModal, setCurrentModal] = useState<string | null>(null)
  const [currentOffset, setCurrentOffset] = useState<number>(0)
  const [currentSearch, setCurrentSearch] = useState<string>("")
  const [currentTab, setCurrentTab] = useState<string>(tab ? tab : TAB_SELF)
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [showTrials, setShowTrials] = useState(false)

  //Load account on start
  useEffect(() => {
    loadAccounts()

    //Load account
    async function loadAccounts() {
      store.dispatch(accountStatus(STATUS_SEARCHING))

      //Do query (get list and count)
      const accounts: any = await store.dispatch(
        accountFetch_AsSupervisor(currentSearch, currentOffset, LIMIT, currentTab === TAB_SELF, showTrials)
      )
      const accountsCount: any = await store.dispatch(
        accountFetchCount_AsSupervisor(currentSearch, currentTab === TAB_SELF, showTrials)
      )
      setIsLoaded(true)

      if (accounts.error || accountsCount.error) {
        store.dispatch(accountStatus(STATUS_LOAD_ERROR))
      } else {
        //Update list
        store.dispatch(accountStatus(STATUS_LOADED))
        store.dispatch(accountGet(accounts))

        //Update store
        store.dispatch(accountGetCountSearch(accountsCount.count))
        if (!currentSearch.length) store.dispatch(accountGetCount(accountsCount.count))
      }
    }
  }, [currentOffset, currentTab, currentSearch, showTrials])

  //Add new account
  function addAccount() {
    store.dispatch(accountAdd(props._session.supervisorId, props._session.userFirstname, props._session.userLastname))
    store.dispatch(subscriptionGet([]))
    setCurrentModal(MODAL_ADD)
  }

  //Delete user
  function deleteUser(user: User) {
    store.dispatch(userActivate(user))
    store.dispatch(accountActivate(user.Account))
    setCurrentModal(MODAL_DELETE_USER)
  }

  //Clic on edit button > get subscriptions and pack
  function editAccount(account: Account) {
    store.dispatch(accountActivate(account))
    store.dispatch(subscriptionGet(account.Subscriptions))
    navigate("/supervisor/account")
  }

  //Clic on "connect to account"
  //Open dialog to confirm
  function getAccount(account: Account) {
    store.dispatch(accountActivate(account))
    setCurrentModal(MODAL_AUTH_CONFIRM)
  }

  //Get access to smartlink account
  function getAccountDemo(isMultiple: boolean) {
    getAccount(isMultiple ? ACCOUNT_DEMO_GROUP : ACCOUNT_DEMO)
  }

  function getTabContent() {
    switch (currentTab) {
      case TAB_SEND:
        return <SendingsAllWidget onClick={(account: Account) => getAccount(account)} />

      default:
        return (
          <SupervisorsListWidget
            onClick={(account: Account) => getAccount(account)}
            onDelete={(user: User) => deleteUser(user)}
          />
        )
    }
  }

  //Search account
  function searchAccounts(search: string) {
    setCurrentSearch(search)
    setCurrentOffset(0)
    setIsLoaded(false)
  }

  //Select tab
  //Reload account after
  function selectTab(tab: string) {
    store.dispatch(accountGet([]))

    setCurrentOffset(0)
    setCurrentTab(tab)
    if (tab !== TAB_USERS) {
      setIsLoaded(false)
    }
  }

  //Change page
  function setPage(offset: number) {
    setCurrentOffset(offset)
    setIsLoaded(false)
  }

  return (
    <NavSupervisor>
      {currentModal === MODAL_ADD && <AddAccountModal onClose={() => setCurrentModal(null)} />}

      {currentModal === MODAL_AUTH_CONFIRM && (
        <SupervisorAccountConnectModal account={props.account.active} onClose={() => setCurrentModal(null)} />
      )}

      {currentModal === MODAL_DELETE_USER && <DeleteUserSuperModal onClose={() => setCurrentModal(null)} />}

      <PageHeader title={t("accounts")}>
        <Button className="primary" onClick={addAccount}>
          {t("account_create")}
        </Button>
      </PageHeader>

      <Tabs
        active={currentTab}
        onClick={selectTab}
        tabs={{
          TAB_SELF: t("account_self"),
          TAB_MED: t("account_med"),
          TAB_SEND: t("sendings"),
          TAB_USERS: t("user_admins_all")
        }}
      />

      {currentTab === TAB_USERS || currentTab === TAB_SEND ? (
        getTabContent()
      ) : (
        <Card isWithoutPadding isOverflowHidden>
          <div style={{ position: "absolute", top: 23, left: 368 }}>
            <Checkbox
              active={showTrials}
              text="Afficher les essais gratuits"
              onClick={() => setShowTrials(!showTrials)}></Checkbox>
          </div>
          <ListContainer
            countLimit={LIMIT}
            countSearch={props.account.countSearch}
            isFooterActive
            onSearch={searchAccounts}
            onSearchStart={() => store.dispatch(accountStatus(STATUS_SEARCHING))}
            onSetPage={setPage}
            status={props.account.status}>
            <PageLoader status={isLoaded ? STATUS_LOADED : STATUS_LOADING}>
              {
                /**
                 * display for all accounts
                 */
                (currentTab === TAB_SELF || currentTab === TAB_MED) && (
                  <div>
                    <ListFields>
                      <ListCell />
                      <ListCell width={200} text={t("user_lastname")} />
                      <ListCell width={260} text={t("project")} />
                      <Space />
                      <ListCell width={300} text="Connexion" />
                      <ListCell width={180} text={t("account_modules")} />
                      <ListCell />
                      <ListCell />
                    </ListFields>

                    {
                      /** connexion to smartlink accounts */
                      currentTab === TAB_SELF && (
                        <div>
                          <ListItem isEditable onClick={() => getAccountDemo(false)}>
                            <ListIcon
                              fallbackIcon={faShieldAlt}
                              iconColor={ACCOUNT_DEMO_COLOR}
                              image={getImageUrl("Accounts", ACCOUNT_DEMO.id, "")}
                            />

                            <ListCell width={200} text={ACCOUNT_DEMO.name} />

                            <Chip color={ACCOUNT_DEMO_COLOR}>{t("account_demo")}</Chip>

                            <Space />

                            <ListButton
                              icon={faPlayCircle}
                              onClick={() => getAccountDemo(false)}
                              isPrimary
                              text={t("utils_connect")}
                            />
                          </ListItem>

                          <ListItem isEditable onClick={() => getAccountDemo(true)}>
                            <ListIcon
                              fallbackIcon={faShieldAlt}
                              iconColor={ACCOUNT_DEMO_COLOR}
                              image={getImageUrl("Accounts", ACCOUNT_DEMO_GROUP.id, "")}
                            />

                            <ListCell width={200} text={ACCOUNT_DEMO_GROUP.name} />

                            <Chip color={ACCOUNT_DEMO_COLOR}>{t("account_demo")} (ETI)</Chip>

                            <Space />

                            <ListButton
                              icon={faPlayCircle}
                              onClick={() => getAccountDemo(true)}
                              isPrimary
                              text={t("utils_connect")}
                            />
                          </ListItem>
                        </div>
                      )
                    }

                    {props.account.list.map((account: Account) => (
                      <ListItem isEditable onClick={() => getAccount(account)} key={account.id}>
                        <ListIcon
                          fallbackIcon={faShieldAlt}
                          iconColor={account.colors.brandPrimary}
                          image={
                            account.options.image
                              ? getImageUrl("Accounts", account.id)
                              : null
                          }
                        />

                        <ListCell width={200} text={account.name} />

                        <div style={{ width: 260 }}>
                          <div className="flex">
                            {!account.options.isDemoAccount && account.Projects[0] ? (
                              <Chip color={account.Projects[0].statusColor}>{account.projectStatus}</Chip>
                            ) : (
                              !account.options.isDemoAccount && account.projectStatus
                            )}
                          </div>

                          {!account.options.isDemoAccount && account.options.onboardingAction && (
                            <div
                              className="grey-t"
                              style={{
                                fontSize: 12,
                                marginTop: account.projectStatus ? 8 : 0
                              }}>
                              Action : <b>{t("home_welcome_" + account.options.onboardingAction)}</b>
                            </div>
                          )}
                        </div>

                        <Space />

                        {!account.options.isDemoAccount && (
                          <ListCell width={300}>
                            <div style={{ fontSize: 12 }}>
                              <div>
                                Création du compte : <b>{account.dateStartLabel}</b>
                              </div>
                              {account.Users[0]?.connectedAt && (
                                <div>
                                  Dernière connexion : <b>{formatDate(account.Users[0].connectedAt, true, false)}</b>
                                </div>
                              )}
                            </div>
                          </ListCell>
                        )}

                        {account.options.isDemoAccount && <ListCell width={300} />}

                        {!account.options.isDemoAccount && (
                          <ListCell width={180}>
                            <ModulesWidget features={account.features} modules={account.modules} readonly />
                          </ListCell>
                        )}

                        {account.options.isDemoAccount && <ListCell width={180} />}

                        <ListButton icon={faPencilAlt} onClick={() => editAccount(account)} text={t("account_edit")} />

                        <ListButton
                          icon={faPlayCircle}
                          onClick={() => getAccount(account)}
                          isPrimary
                          text={t("utils_connect")}
                        />
                      </ListItem>
                    ))}
                  </div>
                )
              }
            </PageLoader>
          </ListContainer>
        </Card>
      )}
    </NavSupervisor>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  account: state.account
})

export default connect(mapStateToProps)(withTranslation()(SupervisorAccounts))
